import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const AboutUs = () => {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: 'Priya Sharma',
      role: 'Co-Founder & CEO',
      background: 'MBA from Wharton, 15 years in wedding industry',
      cultural: 'Punjabi heritage, planned 200+ weddings',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b9e3a56f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Rajesh Patel',
      role: 'Co-Founder & CTO',
      background: 'Former Google engineer, Stanford Computer Science',
      cultural: 'Gujarati traditions, tech innovation for cultural events',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Meera Singh',
      role: 'Head of Cultural Affairs',
      background: 'Cultural anthropologist, wedding tradition expert',
      cultural: 'Sikh ceremonies, regional custom specialist',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Arjun Krishna',
      role: 'Vendor Relations Director',
      background: 'Event management, vendor network building',
      cultural: 'South Indian traditions, multi-cultural wedding expert',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  const companyStats = [
    {
      number: '15,000+',
      label: 'Couples Connected',
      description: 'Successfully matched with perfect vendors'
    },
    {
      number: '2,500+',
      label: 'Verified Vendors',
      description: 'Across 25+ major metropolitan areas'
    },
    {
      number: '8,000+',
      label: 'Weddings Facilitated',
      description: 'Beautiful ceremonies brought to life'
    },
    {
      number: '98%',
      label: 'Customer Satisfaction',
      description: 'Couples who would recommend us'
    }
  ];

  const culturalTraditions = [
    {
      tradition: 'Hindu Ceremonies',
      description: 'Traditional rituals, mandap setups, pandit services',
      icon: 'Sun'
    },
    {
      tradition: 'Sikh Weddings',
      description: 'Gurdwara ceremonies, Anand Karaj traditions',
      icon: 'Star'
    },
    {
      tradition: 'Muslim Nikah',
      description: 'Islamic wedding customs, mehendi celebrations',
      icon: 'Moon'
    },
    {
      tradition: 'Regional Customs',
      description: 'Bengali, Tamil, Gujarati, Punjabi specialties',
      icon: 'Globe'
    }
  ];

  const coreValues = [
    {
      title: 'Authenticity',
      description: 'Preserving traditional customs while embracing modern convenience',
      icon: 'Shield'
    },
    {
      title: 'Quality',
      description: 'Rigorous vendor verification and quality assurance processes',
      icon: 'Award'
    },
    {
      title: 'Cultural Celebration',
      description: 'Honoring diverse South Asian traditions with deep respect',
      icon: 'Heart'
    },
    {
      title: 'Inclusivity',
      description: 'Welcoming all couples regardless of background or tradition',
      icon: 'Users'
    }
  ];

  const trustIndicators = [
    {
      title: 'Vendor Verification',
      description: 'Multi-step verification process including background checks, portfolio review, and reference validation',
      icon: 'CheckCircle'
    },
    {
      title: 'Quality Assurance',
      description: 'Continuous monitoring, customer feedback integration, and performance tracking',
      icon: 'Shield'
    },
    {
      title: 'Customer Protection',
      description: 'Secure payment processing, booking protection, and dispute resolution',
      icon: 'Lock'
    }
  ];

  const handleContactUs = () => {
    navigate('/contact-us');
  };

  const handleFindVendors = () => {
    navigate('/category-browse');
  };

  const handleJoinVendors = () => {
    navigate('/vendor-registration');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About Us - ShaadiNest | Authentic South Asian Wedding Marketplace</title>
        <meta 
          name="description" 
          content="Learn about ShaadiNest's mission to connect South Asian couples with authentic wedding vendors through cultural expertise and modern technology." 
        />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                  Celebrating Love Through Cultural Authenticity
                </h1>
                <p className="text-xl text-text-secondary mb-8">
                  ShaadiNest was founded with a simple yet powerful mission: to help South Asian couples find authentic wedding vendors who truly understand their cultural traditions and can bring their dream wedding to life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    iconName="Search"
                    onClick={handleFindVendors}
                  >
                    Find Vendors
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Mail"
                    onClick={handleContactUs}
                  >
                    Contact Us
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-w-16 aspect-h-12">
                  <img
                    src="https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    alt="South Asian wedding celebration"
                    className="w-full h-96 object-cover rounded-2xl shadow-strong"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-strong p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center">
                      <Icon name="Heart" size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-2xl text-text-primary">8,000+</p>
                      <p className="text-sm text-text-secondary">Weddings Celebrated</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-text-secondary max-w-4xl mx-auto">
                We bridge the gap between traditional South Asian wedding customs and modern technology, creating a platform where couples can discover vendors who don't just provide services, but understand the cultural significance of every ritual and celebration.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={value.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {value.title}
                  </h3>
                  <p className="text-text-secondary">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Statistics */}
        <section className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Growing Together
              </h2>
              <p className="text-lg text-text-secondary">
                Our platform continues to grow as we connect more couples with amazing vendors
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyStats.map((stat, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {stat.label}
                  </div>
                  <p className="text-text-secondary text-sm">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Meet Our Team
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Our diverse team brings together expertise in technology, wedding planning, and deep cultural knowledge
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">
                      {member.name}
                    </h3>
                    <p className="text-primary font-medium mb-3">{member.role}</p>
                    <p className="text-text-secondary text-sm mb-2">{member.background}</p>
                    <p className="text-text-secondary text-sm italic">{member.cultural}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cultural Commitment */}
        <section className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Cultural Expertise
              </h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                We understand that South Asian weddings are not just celebrations, but sacred traditions that connect families and communities across generations
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {culturalTraditions.map((tradition, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={tradition.icon} size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                        {tradition.tradition}
                      </h3>
                      <p className="text-text-secondary">{tradition.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Why Couples Trust Us
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Our commitment to quality and authenticity ensures every couple finds the perfect vendors for their special day
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {trustIndicators.map((indicator, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-success to-success-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={indicator.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                    {indicator.title}
                  </h3>
                  <p className="text-text-secondary">{indicator.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Testimonials */}
        <section className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                What Couples Say
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: 'Sneha & Vikram',
                  location: 'Bay Area, CA',
                  testimonial: 'ShaadiNest helped us find vendors who truly understood our Gujarati traditions. Our wedding was absolutely perfect!',
                  rating: 5
                },
                {
                  name: 'Aisha & Omar',
                  location: 'New York, NY',
                  testimonial: 'The cultural expertise of the platform made our nikah ceremony authentic and beautiful. Highly recommend!',
                  rating: 5
                },
                {
                  name: 'Preet & Jasdeep',
                  location: 'Chicago, IL',
                  testimonial: 'From the gurdwara ceremony to the reception, every vendor understood our Sikh traditions perfectly.',
                  rating: 5
                }
              ].map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                    ))}
                  </div>
                  <p className="text-text-secondary mb-4 italic">"{testimonial.testimonial}"</p>
                  <div>
                    <p className="font-medium text-text-primary">{testimonial.name}</p>
                    <p className="text-sm text-text-secondary">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Ready to Start Your Wedding Journey?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of couples who have found their perfect vendors through ShaadiNest
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Search"
                onClick={handleFindVendors}
              >
                Find Vendors
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="UserPlus"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={handleJoinVendors}
              >
                Join as Vendor
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-6">
                Get in Touch
              </h2>
              <p className="text-lg text-text-secondary">
                Have questions? We'd love to hear from you.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Email Us
                </h3>
                <p className="text-text-secondary">hello@shaadinest.com</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Phone" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Call Us
                </h3>
                <p className="text-text-secondary">(555) 123-4567</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MapPin" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Visit Us
                </h3>
                <p className="text-text-secondary">San Francisco, CA</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;